import React, { useState, useRef, useEffect } from 'react';
import { CreateScenarioDto, ScenarioStatus, ScenarioPersona, CompetencyRubricItem } from '../types';
import { PersonaEditor, LearnerRoleSelector } from './PersonaEditor';
import { ObjectiveEditor } from './ObjectiveEditor';
import { RubricEditor } from './RubricEditor';
import { ScenarioPreview } from './ScenarioPreview';
import { AiScenarioGeneratorModal } from './AiScenarioGeneratorModal';
import { BestCaseScriptModal } from './BestCaseScriptModal';
import { ImportScenarioJsonModal, downloadScenarioTemplateJson } from './ImportScenarioJsonModal';
import { showToast } from '../../../utils/alerts';
import api, { media } from '../../../services/api';
import { getPrimaryLocaleForCountry, inferGenderFromVoiceName } from '../../../services/voiceService';

interface ScenarioFormProps {
  initialValues?: CreateScenarioDto & { status?: ScenarioStatus; id?: string };
  isSubmitting?: boolean;
  onSaveDraft: (data: CreateScenarioDto) => Promise<void>;
  onPublish?: (data: CreateScenarioDto) => Promise<void>;
  onCancel: () => void;
}

const DEFAULT_LANGUAGES = [
  { code: 'en', label: 'English (US / en-US / en-IN)' },
  { code: 'en-GB', label: 'English (UK / en-GB)' },
  { code: 'hi', label: 'Hindi (हिंदी)' },
  { code: 'ta', label: 'Tamil (தமிழ்)' },
  { code: 'te', label: 'Telugu (తెలుగు)' },
  { code: 'kn', label: 'Kannada (ಕನ್ನಡ)' },
  { code: 'mr', label: 'Marathi (मराठी)' },
  { code: 'bn', label: 'Bengali (বাংলা)' },
  { code: 'gu', label: 'Gujarati (ગુજરાતી)' },
  { code: 'ml', label: 'Malayalam (മലയാളം)' },
  { code: 'pa', label: 'Punjabi (ਪੰਜਾਬੀ)' },
  { code: 'es', label: 'Spanish (Español)' },
  { code: 'fr', label: 'French (Français)' },
  { code: 'de', label: 'German (Deutsch)' },
  { code: 'ar', label: 'Arabic (العربية)' },
  { code: 'ja', label: 'Japanese (日本語)' },
];

export const ScenarioForm: React.FC<ScenarioFormProps> = ({
  initialValues,
  isSubmitting = false,
  onSaveDraft,
  onPublish,
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'persona' | 'objectives' | 'rubric' | 'settings' | 'preview'>('basic');
  const [isGeneratorOpen, setIsGeneratorOpen] = useState<boolean>(false);
  const [isBestCaseScriptOpen, setIsBestCaseScriptOpen] = useState<boolean>(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [customLangInput, setCustomLangInput] = useState<string>('');

  const defaultPersona: ScenarioPersona = {
    name: 'Priya Sharma',
    role: 'Customer',
    startingMood: 'Frustrated',
    openingStatement: "I've been waiting three days for this double charge on my account to be resolved. Why is it taking so long?",
    personaObjective: 'Explain the problem clearly, express frustration about the delay, demand ownership, and expect a clear next step.',
    hiddenFact: 'The customer issue can be resolved quickly once ownership is established.',
    genderPreference: 'female',
    avatarUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80',
    avatarType: 'gif'
  };

  const DEFAULT_RUBRICS: Record<string, CompetencyRubricItem> = {
    EMPATHY: { name: 'Empathy', weight: 0.2, description: 'Acknowledges customer feelings promptly and warmly.' },
    PROBLEM_UNDERSTANDING: { name: 'Problem Understanding', weight: 0.2, description: 'Clarifies key issues and root causes.' },
    OWNERSHIP: { name: 'Ownership', weight: 0.2, description: 'Takes personal responsibility rather than deflecting.' },
    COMMUNICATION: { name: 'Communication Clarity', weight: 0.2, description: 'Speaks clearly and concisely in a professional tone.' },
    RESOLUTION: { name: 'Next-Step Management', weight: 0.2, description: 'Sets realistic expectations without false promises.' },
  };

  const initialRubrics = (initialValues?.rubrics && typeof initialValues.rubrics === 'object' && Object.keys(initialValues.rubrics).length > 0)
    ? initialValues.rubrics
    : DEFAULT_RUBRICS;

  const [tagInput, setTagInput] = useState<string>('');
  const [policyInput, setPolicyInput] = useState<string>('');
  const [docInput, setDocInput] = useState<string>('');
  const [isUploadingDoc, setIsUploadingDoc] = useState<boolean>(false);
  const docFileInputRef = useRef<HTMLInputElement | null>(null);

  // Organization-level governance policy check
  const [organizationAllowsVideoAnalysis, setOrganizationAllowsVideoAnalysis] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    async function checkOrgPolicy() {
      try {
        const res: any = await api.get('/api/v1/admin/tenant/settings');
        if (active && res) {
          const policy = res.data?.policy || res.data || res.policy;
          if (policy && policy.videoAnalysisEnabled !== undefined) {
            setOrganizationAllowsVideoAnalysis(Boolean(policy.videoAnalysisEnabled));
          }
        }
      } catch {
        // Fallback gracefully for trainer role
      }
    }
    checkOrgPolicy();
    return () => { active = false; };
  }, []);

  const [formData, setFormData] = useState<CreateScenarioDto>({
    title: initialValues?.title || '',
    description: initialValues?.description || '',
    category: initialValues?.category || 'Customer Support',
    difficulty: initialValues?.difficulty || 'INTERMEDIATE',
    learnerRole: initialValues?.learnerRole || 'Customer Support Representative',
    learnerObjective: initialValues?.learnerObjective || '',
    estimatedMinutes: initialValues?.estimatedMinutes || 15,
    timeLimitMinutes: initialValues?.timeLimitMinutes || 10,
    timeOutAction: initialValues?.timeOutAction || 'WARN_1MIN',
    tags: initialValues?.tags || [],
    startsAt: initialValues?.startsAt || '',
    dueAt: initialValues?.dueAt || '',
    sendLearnerReminders: initialValues?.sendLearnerReminders !== undefined ? Boolean(initialValues.sendLearnerReminders) : true,
    reminderSchedule: initialValues?.reminderSchedule || '24H_AND_2H_BEFORE',
    reminderChannels: initialValues?.reminderChannels || ['EMAIL', 'IN_APP'],
    notifyManagerOnOverdue: initialValues?.notifyManagerOnOverdue !== undefined ? Boolean(initialValues.notifyManagerOnOverdue) : true,
    maxAttempts: initialValues?.maxAttempts || 3,
    attemptGapHours: initialValues?.attemptGapHours !== undefined ? initialValues.attemptGapHours : 0,
    allowResume: initialValues?.allowResume !== undefined ? Boolean(initialValues.allowResume) : true,
    startingCountsAsAttempt: initialValues?.startingCountsAsAttempt !== undefined ? Boolean(initialValues.startingCountsAsAttempt) : true,
    passScore: initialValues?.passScore || 70,
    releaseResultsTiming: initialValues?.releaseResultsTiming || 'IMMEDIATELY',
    scoreCountingPolicy: initialValues?.scoreCountingPolicy || 'BEST',
    allowedLanguages: initialValues?.allowedLanguages || ['en', 'hi', 'ta'],
    allowMixedLanguageReplies: initialValues?.allowMixedLanguageReplies !== undefined ? Boolean(initialValues.allowMixedLanguageReplies) : true,
    feedbackLanguage: initialValues?.feedbackLanguage || 'en',
    companyPolicies: initialValues?.companyPolicies || [],
    referenceDocuments: initialValues?.referenceDocuments || [],
    guidelines: initialValues?.guidelines || [
      'Acknowledge the customer frustration promptly with genuine empathy.',
      'Ask clarifying questions to confirm the root problem.',
      'Take personal ownership without making unsupported promises.',
    ],
    objectives: initialValues?.objectives || [
      { id: 'obj_1', title: 'Acknowledge customer frustration with genuine empathy', required: true },
      { id: 'obj_2', title: 'Confirm the core problem and history', required: true },
      { id: 'obj_3', title: 'Take personal ownership and outline realistic next steps', required: true },
    ],
    persona: initialValues?.persona || defaultPersona,
    personas: initialValues?.personas || (initialValues?.persona ? [initialValues.persona] : [defaultPersona]),
    rubrics: initialRubrics,
    requiresCamera: initialValues?.requiresCamera !== undefined
      ? Boolean(initialValues.requiresCamera)
      : (initialValues?.hardwareRequirements?.requiresCamera !== undefined
          ? Boolean(initialValues.hardwareRequirements.requiresCamera)
          : true),
    requiresMicrophone: initialValues?.requiresMicrophone !== undefined
      ? Boolean(initialValues.requiresMicrophone)
      : (initialValues?.hardwareRequirements?.requiresMicrophone !== undefined
          ? Boolean(initialValues.hardwareRequirements.requiresMicrophone)
          : true),
    requiresSpeaker: initialValues?.requiresSpeaker !== undefined
      ? Boolean(initialValues.requiresSpeaker)
      : (initialValues?.hardwareRequirements?.requiresSpeaker !== undefined
          ? Boolean(initialValues.hardwareRequirements.requiresSpeaker)
          : true),
    videoAnalysisEnabled: initialValues?.videoAnalysisEnabled !== undefined
      ? Boolean(initialValues.videoAnalysisEnabled)
      : (initialValues?.briefingContent?.videoAnalysisEnabled !== undefined
          ? Boolean(initialValues.briefingContent.videoAnalysisEnabled)
          : false),
    scenarioBackground: initialValues?.scenarioBackground || '',
    scenarioContext: initialValues?.scenarioContext || '',
    simulationType: (initialValues as any)?.simulationType || 'STANDARD',
    attemptAllowance: (initialValues as any)?.attemptAllowance || ((initialValues as any)?.simulationType === 'PREMIUM' ? 10 : 3),
  });

  const [formError, setFormError] = useState<string | null>(null);

  // Synchronize form state when initialValues change or load asynchronously
  useEffect(() => {
    if (!initialValues) return;
    const simType = (initialValues as any).simulationType || 'STANDARD';
    const allowance = (initialValues as any).attemptAllowance !== undefined
      ? Number((initialValues as any).attemptAllowance)
      : (simType === 'PREMIUM' ? 10 : 3);

    setFormData((prev) => ({
      ...prev,
      title: initialValues.title !== undefined ? initialValues.title : prev.title,
      description: initialValues.description !== undefined ? initialValues.description : prev.description,
      category: initialValues.category || prev.category,
      difficulty: initialValues.difficulty || prev.difficulty,
      learnerRole: initialValues.learnerRole || prev.learnerRole,
      learnerObjective: initialValues.learnerObjective || prev.learnerObjective,
      estimatedMinutes: initialValues.estimatedMinutes || prev.estimatedMinutes,
      timeLimitMinutes: initialValues.timeLimitMinutes || prev.timeLimitMinutes,
      maxAttempts: initialValues.maxAttempts || allowance,
      passScore: initialValues.passScore !== undefined ? initialValues.passScore : prev.passScore,
      allowedLanguages: initialValues.allowedLanguages || prev.allowedLanguages,
      guidelines: initialValues.guidelines || prev.guidelines,
      objectives: initialValues.objectives || prev.objectives,
      persona: initialValues.persona || prev.persona,
      personas: initialValues.personas || prev.personas,
      rubrics: (initialValues.rubrics && Object.keys(initialValues.rubrics).length > 0) ? initialValues.rubrics : prev.rubrics,
      scenarioBackground: initialValues.scenarioBackground || prev.scenarioBackground,
      scenarioContext: initialValues.scenarioContext || prev.scenarioContext,
      simulationType: simType,
      attemptAllowance: allowance,
    }));
  }, [initialValues]);

  const setSimulationTier = (tier: 'STANDARD' | 'PREMIUM') => {
    const allowance = tier === 'PREMIUM' ? 10 : 3;
    setFormData((prev) => {
      const curPersona = prev.persona || defaultPersona;
      const isMale = curPersona.genderPreference === 'male';
      const updatedPersona = tier === 'PREMIUM' && curPersona.ttsProvider === 'browser'
        ? {
            ...curPersona,
            ttsProvider: 'openai' as const,
            ttsVoice: curPersona.ttsVoice || (isMale ? 'onyx' : 'nova'),
          }
        : curPersona;

      const updatedPersonas = Array.isArray(prev.personas) && prev.personas.length > 0
        ? prev.personas.map((p, idx) => (idx === 0 ? updatedPersona : p))
        : [updatedPersona];

      return {
        ...prev,
        simulationType: tier,
        attemptAllowance: allowance,
        maxAttempts: allowance,
        persona: updatedPersona,
        personas: updatedPersonas,
        ttsProvider: updatedPersona.ttsProvider,
        ttsVoice: updatedPersona.ttsVoice,
      };
    });
  };

  const updateField = (field: keyof CreateScenarioDto, val: any) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    const current = formData.tags || [];
    if (!current.includes(trimmed)) {
      updateField('tags', [...current, trimmed]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    const current = formData.tags || [];
    updateField('tags', current.filter((t) => t !== tag));
  };

  const handleAddPolicy = () => {
    const trimmed = policyInput.trim();
    if (!trimmed) return;
    const current = formData.companyPolicies || [];
    if (!current.includes(trimmed)) {
      updateField('companyPolicies', [...current, trimmed]);
    }
    setPolicyInput('');
  };

  const handleRemovePolicy = (item: string) => {
    const current = formData.companyPolicies || [];
    updateField('companyPolicies', current.filter((p) => p !== item));
  };

  const handleAddDoc = () => {
    const trimmed = docInput.trim();
    if (!trimmed) return;
    const current = formData.referenceDocuments || [];
    if (!current.includes(trimmed)) {
      updateField('referenceDocuments', [...current, trimmed]);
    }
    setDocInput('');
  };

  const handleRemoveDoc = (item: string) => {
    const current = formData.referenceDocuments || [];
    updateField('referenceDocuments', current.filter((d) => d !== item));
  };

  const handleDocFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      showToast('File size exceeds maximum 15MB limit.', 'error');
      return;
    }

    try {
      setIsUploadingDoc(true);
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result as string;
          const res = await media.upload({
            file: base64Data,
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            resourceType: 'auto',
            folder: 'scenario-documents',
          });

          if (res && res.data && res.data.url) {
            const publicUrl = res.data.url;
            const current = formData.referenceDocuments || [];
            updateField('referenceDocuments', [...current, publicUrl]);
            showToast(`Document "${file.name}" uploaded to Cloudinary/Cloud Storage successfully!`, 'success');
          } else {
            showToast('Upload completed, but no public file URL was returned.', 'error');
          }
        } catch (uploadErr: any) {
          showToast(uploadErr.message || 'Failed to upload document file.', 'error');
        } finally {
          setIsUploadingDoc(false);
          if (docFileInputRef.current) {
            docFileInputRef.current.value = '';
          }
        }
      };
      reader.onerror = () => {
        setIsUploadingDoc(false);
        showToast('Failed to read file from disk.', 'error');
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setIsUploadingDoc(false);
      showToast(err.message || 'Failed to process document upload.', 'error');
    }
  };

  const handleToggleLanguage = (lang: string) => {
    const current = formData.allowedLanguages || ['en'];
    if (current.includes(lang)) {
      if (current.length > 1) {
        updateField('allowedLanguages', current.filter((l) => l !== lang));
      }
    } else {
      updateField('allowedLanguages', [...current, lang]);
    }
  };

  const handleAddCustomLanguage = () => {
    const trimmed = customLangInput.trim();
    if (!trimmed) return;
    const current = formData.allowedLanguages || ['en'];
    if (!current.includes(trimmed)) {
      updateField('allowedLanguages', [...current, trimmed]);
    }
    setCustomLangInput('');
  };

  const handleRemoveCustomLanguage = (lang: string) => {
    const current = formData.allowedLanguages || ['en'];
    if (current.length > 1) {
      updateField('allowedLanguages', current.filter((l) => l !== lang));
    }
  };

  const handleApplyBlueprint = (blueprint: any) => {
    setFormData((prev) => {
      const currentPersona = prev.persona || defaultPersona;
      const blueprintRubrics = (blueprint.rubrics && typeof blueprint.rubrics === 'object' && Object.keys(blueprint.rubrics).length > 0)
        ? blueprint.rubrics
        : prev.rubrics && Object.keys(prev.rubrics).length > 0
          ? prev.rubrics
          : DEFAULT_RUBRICS;

      const appliedPersona = {
        ...currentPersona,
        name: blueprint.persona?.name || currentPersona.name,
        role: blueprint.persona?.role || currentPersona.role,
        skills: Array.isArray(blueprint.persona?.skills) ? blueprint.persona.skills : (currentPersona.skills || []),
        startingMood: blueprint.persona?.mood || blueprint.persona?.startingMood || currentPersona.startingMood,
        openingStatement: blueprint.persona?.openingStatement || currentPersona.openingStatement,
        personaObjective: blueprint.persona?.personaObjective || (Array.isArray(blueprint.persona?.behavioralDirectives) ? blueprint.persona.behavioralDirectives.join('\n') : currentPersona.personaObjective),
        hiddenFact: blueprint.persona?.hiddenFact || currentPersona.hiddenFact,
        whatTheyWant: blueprint.persona?.whatTheyWant || currentPersona.whatTheyWant || '',
        hiddenConcern: blueprint.persona?.hiddenConcern || currentPersona.hiddenConcern || '',
        objections: Array.isArray(blueprint.persona?.objections) ? blueprint.persona.objections : (currentPersona.objections || []),
        whatCalmsThemDown: blueprint.persona?.whatCalmsThemDown || currentPersona.whatCalmsThemDown || '',
        theyAgreeWhen: blueprint.persona?.theyAgreeWhen || currentPersona.theyAgreeWhen || '',
        avatarUrl: blueprint.persona?.avatarUrl || currentPersona.avatarUrl,
        avatarType: blueprint.persona?.avatarType || currentPersona.avatarType || 'gif',
        genderPreference: blueprint.persona?.genderPreference || currentPersona.genderPreference,
      };

      const appliedPersonas = Array.isArray(blueprint.personas) && blueprint.personas.length > 0
        ? blueprint.personas
        : [appliedPersona];

      return {
        ...prev,
        title: blueprint.title || prev.title,
        description: blueprint.description || prev.description,
        category: blueprint.category || prev.category,
        difficulty: (blueprint.difficultyLevel?.toUpperCase() || blueprint.difficulty?.toUpperCase() || prev.difficulty) as any,
        learnerRole: blueprint.learnerRole || prev.learnerRole,
        learnerObjective: blueprint.learnerObjective || prev.learnerObjective || '',
        persona: appliedPersona,
        personas: appliedPersonas,
        companyPolicies: blueprint.companyPolicies || prev.companyPolicies || [],
        referenceDocuments: blueprint.referenceDocuments || prev.referenceDocuments || [],
        guidelines: blueprint.guidelines || [
          'Acknowledge customer needs promptly and empathetically.',
          'Address constraints and objections with domain transparency.',
          'Establish clear next steps and follow-up timeline.'
        ],
        objectives: blueprint.objectives || (blueprint.stages ? blueprint.stages.map((stg: any, idx: number) => ({
          id: `obj_${idx + 1}`,
          title: stg.goal || stg.title,
          required: true
        })) : prev.objectives),
        rubrics: blueprintRubrics,
      };
    });
  };

  const validateBasic = (): boolean => {
    if (!formData.title.trim()) {
      setFormError('Scenario Title is required.');
      setActiveTab('basic');
      return false;
    }
    if (!formData.description.trim()) {
      setFormError('Scenario Description is required.');
      setActiveTab('basic');
      return false;
    }
    setFormError(null);
    return true;
  };

  const getPreparedPayload = (): CreateScenarioDto => {
    let currentRubrics = formData.rubrics;
    if (!currentRubrics || typeof currentRubrics !== 'object' || Object.keys(currentRubrics).length === 0) {
      currentRubrics = DEFAULT_RUBRICS;
    }

    // Ensure all rubrics have valid positive weights
    const keys = Object.keys(currentRubrics);
    const totalWeight = keys.reduce((sum, k) => sum + (currentRubrics[k]?.weight || 0), 0);
    const normalizedRubrics: Record<string, CompetencyRubricItem> = {};

    keys.forEach((k) => {
      const item = currentRubrics[k];
      const rawWeight = item?.weight && item.weight > 0 ? item.weight : 1 / keys.length;
      normalizedRubrics[k] = {
        name: item?.name || k,
        description: item?.description || 'Evaluates performance on this competency.',
        weight: totalWeight > 1.5 ? Number((rawWeight / 100).toFixed(3)) : rawWeight,
      };
    });

    const {
      id: _id,
      tenantId: _tenantId,
      tenant_id: _tenant_id,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      ...cleanFormData
    } = formData as any;

    const simType = (formData as any).simulationType || 'STANDARD';
    const allowance = (formData as any).attemptAllowance !== undefined
      ? Number((formData as any).attemptAllowance)
      : (simType === 'PREMIUM' ? 10 : 3);
    const maxAttempts = (formData as any).maxAttempts !== undefined
      ? Number((formData as any).maxAttempts)
      : allowance;

    const primaryPersona = formData.persona || (formData.personas && formData.personas[0]) || defaultPersona;
    const resolvedCountry = primaryPersona.country || (formData as any).country || 'IN';
    const resolvedCountryName = primaryPersona.countryName || (formData as any).countryName || 'India';
    const resolvedTone = primaryPersona.tone || (formData as any).tone || 'Empathetic & Cooperative';
    const resolvedVoice = primaryPersona.ttsVoice || (formData as any).ttsVoice || '';
    const resolvedProvider = primaryPersona.ttsProvider || (formData as any).ttsProvider || 'browser';
    const resolvedPace = primaryPersona.speakingPace !== undefined ? primaryPersona.speakingPace : ((formData as any).speakingPace !== undefined ? (formData as any).speakingPace : 1.0);
    const resolvedPitch = primaryPersona.pitchPreference !== undefined ? primaryPersona.pitchPreference : ((formData as any).pitchPreference !== undefined ? (formData as any).pitchPreference : 1.0);
    const resolvedAvatarUrl = primaryPersona.avatarUrl || (formData as any).avatarUrl || null;
    const resolvedAvatarType = primaryPersona.avatarType || (formData as any).avatarType || 'gif';
    const resolvedGender = primaryPersona.genderPreference
      || (formData as any).genderPreference
      || inferGenderFromVoiceName(resolvedVoice)
      || 'female';

    // The persona tab owns guardrails / ending rules. Treat the persona value as
    // authoritative whenever it is present (including an explicit empty/null which
    // means "author removed them") and only fall back to the stale root copy for
    // legacy payloads that never carried a persona-level field.
    const pickAuthoritative = (personaVal: any, rootVal: any) =>
      personaVal === undefined ? rootVal ?? null : personaVal ?? null;
    const resolvedGuardrails = pickAuthoritative(primaryPersona.roleLockAndGuardrails, (formData as any).roleLockAndGuardrails);
    const resolvedEndingRules = pickAuthoritative(primaryPersona.endingRules, (formData as any).endingRules);

    // Keep a bare "en" out of the TTS contract: an authored full locale wins,
    // otherwise the locale is derived from the persona's country accent.
    const authoredLanguage = (primaryPersona as any).voiceConfig?.language || '';
    const resolvedLanguage = authoredLanguage.includes('-')
      ? authoredLanguage
      : getPrimaryLocaleForCountry(resolvedCountry);

    const resolvedVoiceConfig = {
      ...((primaryPersona as any).voiceConfig || {}),
      provider: resolvedProvider,
      voiceId: resolvedVoice,
      language: resolvedLanguage,
      speakingRate: resolvedPace,
      pitch: resolvedPitch,
      style: resolvedTone,
      genderPreference: resolvedGender,
    };

    const synchronizedPersona = {
      ...primaryPersona,
      country: resolvedCountry,
      countryName: resolvedCountryName,
      tone: resolvedTone,
      ttsVoice: resolvedVoice,
      ttsProvider: resolvedProvider,
      speakingPace: resolvedPace,
      pitchPreference: resolvedPitch,
      avatarUrl: resolvedAvatarUrl,
      avatarType: resolvedAvatarType,
      genderPreference: resolvedGender,
      roleLockAndGuardrails: resolvedGuardrails,
      endingRules: resolvedEndingRules,
      voiceConfig: resolvedVoiceConfig,
    };

    const synchronizedPersonas = Array.isArray(formData.personas) && formData.personas.length > 0
      ? formData.personas.map((p, idx) => (idx === 0 ? synchronizedPersona : p))
      : [synchronizedPersona];

    return {
      ...cleanFormData,
      persona: synchronizedPersona,
      personas: synchronizedPersonas,
      avatarUrl: resolvedAvatarUrl,
      avatarType: resolvedAvatarType,
      genderPreference: resolvedGender,
      country: resolvedCountry,
      countryName: resolvedCountryName,
      tone: resolvedTone,
      ttsVoice: resolvedVoice,
      ttsProvider: resolvedProvider,
      speakingPace: resolvedPace,
      pitchPreference: resolvedPitch,
      roleLockAndGuardrails: resolvedGuardrails,
      endingRules: resolvedEndingRules,
      voiceConfig: resolvedVoiceConfig,
      simulationType: simType,
      attemptAllowance: allowance,
      maxAttempts: maxAttempts,
      requiresCamera: Boolean(formData.requiresCamera),
      requiresMicrophone: Boolean(formData.requiresMicrophone),
      requiresSpeaker: Boolean(formData.requiresSpeaker),
      videoAnalysisEnabled: Boolean(formData.videoAnalysisEnabled && organizationAllowsVideoAnalysis),
      hardwareRequirements: {
        requiresCamera: Boolean(formData.requiresCamera),
        requiresMicrophone: Boolean(formData.requiresMicrophone),
        requiresSpeaker: Boolean(formData.requiresSpeaker),
      },
      rubrics: normalizedRubrics,
    };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateBasic()) return;
    const payload = getPreparedPayload();
    try {
      await onSaveDraft(payload);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save scenario.');
    }
  };

  const handlePublishClick = async () => {
    if (!validateBasic()) return;
    const payload = getPreparedPayload();
    if (onPublish) {
      try {
        await onPublish(payload);
      } catch (err: any) {
        setFormError(err?.message || 'Failed to publish scenario.');
      }
    }
  };

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      {/* Studio Action Toolbar */}
      <div
        className="scenario-toolkit-bar"
        style={{
          padding: '14px 20px',
          borderRadius: '12px',
          background: '#FFFFFF',
          border: '1px solid var(--line, #E2E8F0)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 'min(100%, 240px)', flex: '1 1 auto' }}>
          <span style={{ fontSize: '20px', flexShrink: 0 }}>⚡</span>
          <div>
            <strong style={{ fontSize: '13.5px', color: 'var(--navy, #0F172A)', display: 'block' }}>
              Scenario Authoring &amp; Synthesis Toolkit
            </strong>
            <p style={{ fontSize: '12px', color: 'var(--muted, #64748B)', margin: '1px 0 0' }}>
              Synthesize 6-section case blueprints, import structured JSON, or calibrate scoring rubrics with scripts.
            </p>
          </div>
        </div>

        <div className="scenario-toolkit-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => downloadScenarioTemplateJson(initialValues?.id ? formData : undefined)}
            style={{
              padding: '7px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              color: '#334155',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
            title={initialValues?.id ? "Export current scenario configuration to JSON" : "Download clean sample template"}
          >
            <span>📥</span> {initialValues?.id ? 'Export JSON' : 'Template'}
          </button>
          <button
            type="button"
            onClick={() => setIsImportModalOpen(true)}
            style={{
              padding: '7px 12px',
              borderRadius: '8px',
              border: '1px solid #93C5FD',
              backgroundColor: '#EFF6FF',
              color: '#1D4ED8',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <span>📋</span> Import JSON
          </button>
          <button
            type="button"
            onClick={() => setIsBestCaseScriptOpen(true)}
            style={{
              padding: '7px 14px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              color: '#334155',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>📄</span> Best-Case Script
          </button>
          <button
            type="button"
            onClick={() => setIsGeneratorOpen(true)}
            style={{
              padding: '7px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #DB2777 100%)',
              color: '#FFFFFF',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)',
            }}
          >
            <span>✨</span> AI Generator
          </button>

          <div className="scenario-toolkit-divider" style={{ width: '1px', height: '24px', background: 'var(--line, #E2E8F0)', margin: '0 4px' }} />

          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              padding: '7px 16px',
              borderRadius: '8px',
              border: '1px solid var(--blue, #0D4FBD)',
              backgroundColor: 'var(--blue-soft, #E8F0FE)',
              color: 'var(--blue, #0D4FBD)',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>💾</span> {isSubmitting ? 'Saving…' : 'Save Draft'}
          </button>

          {onPublish && (
            <button
              type="button"
              onClick={handlePublishClick}
              disabled={isSubmitting}
              style={{
                padding: '7px 18px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--blue, #0D4FBD)',
                color: '#FFFFFF',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(13, 79, 189, 0.25)',
              }}
            >
              <span>🚀</span> Publish Scenario →
            </button>
          )}
        </div>
      </div>

      {formError && (
        <div className="admin-alert admin-alert--error" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{formError}</span>
          <button type="button" onClick={() => setFormError(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 700 }}>
            ✕
          </button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div
        className="tabs scenario-form-tabs"
        role="tablist"
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--line)',
          paddingBottom: '4px',
          overflowX: 'auto',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {[
          { id: 'basic', label: '1. Basic Info' },
          { id: 'persona', label: '2. AI Persona & Avatar' },
          { id: 'objectives', label: '3. Objectives' },
          { id: 'rubric', label: '4. Evaluation Rubric' },
          { id: 'settings', label: '5. Settings & Languages' },
          { id: 'preview', label: '👁 Live Preview' },
        ].map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className="tab"
            type="button"
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={() => setActiveTab(tab.id as any)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Form Content */}
      <form onSubmit={handleSave} style={{ display: 'grid', gap: '20px' }}>
        {/* TAB 1: BASIC INFO & CALL POLICIES */}
        {activeTab === 'basic' && (
          <div style={{ display: 'grid', gap: '20px' }}>
            {/* Monetization Tier & Attempt Allowance Selector */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '20px', display: 'grid', gap: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                    Simulation Monetization Tier &amp; Attempt Allowance
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: 'var(--muted)' }}>
                    Creation credit is deducted atomically upon saving. Attempt limits are immutable per scenario.
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '14px' }}>
                {/* Standard Option */}
                <div
                  onClick={() => setSimulationTier('STANDARD')}
                  style={{
                    border: (formData as any).simulationType === 'STANDARD' ? '2px solid var(--blue)' : '1px solid var(--line)',
                    background: (formData as any).simulationType === 'STANDARD' ? 'var(--blue-soft)' : 'var(--surface)',
                    boxShadow: (formData as any).simulationType === 'STANDARD' ? '0 0 0 1px var(--blue)' : 'none',
                    borderRadius: '10px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '14.5px', color: 'var(--navy)' }}>Standard Simulation</strong>
                      {(formData as any).simulationType === 'STANDARD' && (
                        <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px', background: 'var(--blue)', color: '#FFFFFF' }}>
                          ✓ Selected
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        background: 'var(--blue-soft)',
                        color: 'var(--blue)',
                        border: '1px solid rgba(13,79,189,0.2)',
                      }}
                    >
                      3 Attempts Total
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)' }}>
                    Consumes 1 Standard Creation Credit. Includes 3 learner attempts, standard voices, and automated rubric evaluation.
                  </p>
                </div>

                {/* Premium Option */}
                <div
                  onClick={() => setSimulationTier('PREMIUM')}
                  style={{
                    border: (formData as any).simulationType === 'PREMIUM' ? '2px solid #7E22CE' : '1px solid var(--line)',
                    background: (formData as any).simulationType === 'PREMIUM' ? '#FAF5FF' : 'var(--surface)',
                    boxShadow: (formData as any).simulationType === 'PREMIUM' ? '0 0 0 1px #7E22CE' : 'none',
                    borderRadius: '10px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '14.5px', color: '#581C87' }}>Premium Simulation ⭐</strong>
                      {(formData as any).simulationType === 'PREMIUM' && (
                        <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px', background: '#7E22CE', color: '#FFFFFF' }}>
                          ✓ Selected
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        background: 'var(--ok-soft)',
                        color: 'var(--ok)',
                        border: '1px solid rgba(15,138,95,0.2)',
                      }}
                    >
                      10 Attempts Total
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)' }}>
                    Consumes 1 Premium Creation Credit. Grants 10 learner attempts, unlocks OpenAI Speech TTS synthesis, and priority multi-turn LLMs.
                  </p>
                </div>
              </div>
            </div>

            {/* Core Identification */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
                1. Scenario Identification & Categorization
              </h3>

              <div className="admin-form__group">
                <label className="admin-form__label">Scenario Title *</label>
                <input
                  type="text"
                  className="admin-form__input"
                  placeholder="e.g. Sulochana Iyer — FD Renewal (19-Yr Customer)"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  required
                />
              </div>

              <div className="admin-form__group">
                <label className="admin-form__label">Short Description / Briefing *</label>
                <textarea
                  rows={3}
                  className="admin-form__textarea"
                  placeholder="Describe the scenario context, customer expectations, and general simulation background..."
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                <div className="admin-form__group">
                  <label className="admin-form__label">Industry / Category</label>
                  <select
                    className="admin-form__select"
                    value={formData.category}
                    onChange={(e) => updateField('category', e.target.value)}
                  >
                    <option value="Banking & Wealth">Branch Banking & Wealth Management</option>
                    <option value="Customer Support">Fintech & Customer Support</option>
                    <option value="B2B Sales">B2B Solution Sales</option>
                    <option value="HR & Leadership">HR & Difficult Conversations</option>
                    <option value="Healthcare">Healthcare & Patient Intake</option>
                    <option value="Retail & Hospitality">Retail & Hospitality</option>
                  </select>
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Difficulty Tier</label>
                  <select
                    className="admin-form__select"
                    value={formData.difficulty}
                    onChange={(e) => updateField('difficulty', e.target.value as any)}
                  >
                    <option value="BEGINNER">Beginner (Cooperative / Explicit Needs)</option>
                    <option value="INTERMEDIATE">Intermediate (Pragmatic / Needs Probing)</option>
                    <option value="ADVANCED">Advanced (Skeptical / High Friction)</option>
                  </select>
                </div>
              </div>

              {/* Tags Chips Input */}
              <div className="admin-form__group">
                <label className="admin-form__label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                  <span>Admin & Organization Tags</span>
                  <span style={{ fontSize: '11px', color: '#64748B' }}>Add tags to organize scenarios by department, team, or track</span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="admin-form__input"
                    placeholder="e.g. Complaint Handling, Wealth, Retention, Level-2..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="admin-btn admin-btn--secondary"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    + Add Tag
                  </button>
                </div>
                {formData.tags && formData.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {formData.tags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '16px',
                          backgroundColor: '#F1F5F9',
                          color: '#334155',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid #CBD5E1',
                        }}
                      >
                        🏷️ {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontWeight: 700, padding: 0 }}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Schedule & Availability */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
                    2. Schedule & Learner Availability Window
                  </h3>
                  <p style={{ fontSize: '12px', color: '#64748B', margin: '2px 0 0' }}>
                    Control when learners can access, practice, or submit attempts for this simulation.
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                <div className="admin-form__group">
                  <label className="admin-form__label">Available From (Start Date & Time)</label>
                  <input
                    type="datetime-local"
                    className="admin-form__input"
                    value={formData.startsAt || ''}
                    onChange={(e) => updateField('startsAt', e.target.value)}
                  />
                  <span style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', display: 'block' }}>
                    Leave empty for immediate availability upon publishing.
                  </span>
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Due By (Submission Deadline)</label>
                  <input
                    type="datetime-local"
                    className="admin-form__input"
                    value={formData.dueAt || ''}
                    onChange={(e) => updateField('dueAt', e.target.value)}
                  />
                  <span style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', display: 'block' }}>
                    Learners cannot start new attempts after this deadline.
                  </span>
                </div>
              </div>

              {/* Learner Reminders & Notification Dispatch */}
              <div
                style={{
                  marginTop: '12px',
                  borderRadius: '12px',
                  border: formData.sendLearnerReminders ? '1px solid #C7D2FE' : '1px solid #E2E8F0',
                  backgroundColor: formData.sendLearnerReminders ? '#F8FAFF' : '#F8FAFC',
                  padding: '20px',
                  display: 'grid',
                  gap: '16px',
                  transition: 'all 0.2s ease',
                  boxShadow: formData.sendLearnerReminders ? '0 2px 10px rgba(99, 102, 241, 0.05)' : 'none',
                }}
              >
                {/* Header with Switch */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '10px',
                        backgroundColor: formData.sendLearnerReminders ? '#EEF2FF' : '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        color: formData.sendLearnerReminders ? '#4F46E5' : '#94A3B8',
                        border: formData.sendLearnerReminders ? '1px solid #C7D2FE' : '1px solid #E2E8F0',
                      }}
                    >
                      🔔
                    </div>
                    <div>
                      <h4 style={{ fontSize: '13.5px', fontWeight: 600, color: '#0F172A', margin: 0 }}>
                        Automated Learner Reminders & Deadline Alerts
                      </h4>
                      <p style={{ fontSize: '11.5px', color: '#64748B', margin: '2px 0 0' }}>
                        Dispatches automated notifications before deadline to maximize completion rates.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        backgroundColor: formData.sendLearnerReminders ? '#DCFCE7' : '#F1F5F9',
                        color: formData.sendLearnerReminders ? '#166534' : '#64748B',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      <span
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: formData.sendLearnerReminders ? '#22C55E' : '#94A3B8',
                        }}
                      />
                      {formData.sendLearnerReminders ? 'ACTIVE NOTIFICATIONS' : 'MUTED'}
                    </span>

                    <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={formData.sendLearnerReminders ?? true}
                        onChange={(e) => updateField('sendLearnerReminders', e.target.checked)}
                        style={{ width: '18px', height: '18px', accentColor: '#4F46E5', cursor: 'pointer' }}
                      />
                    </label>
                  </div>
                </div>

                {formData.sendLearnerReminders && (
                  <div
                    style={{
                      display: 'grid',
                      gap: '16px',
                      paddingTop: '14px',
                      borderTop: '1px solid #E2E8F0',
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      {/* Reminder Cadence */}
                      <div className="admin-form__group" style={{ margin: 0 }}>
                        <label className="admin-form__label" style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                          ⏰ Reminder Cadence & Trigger
                        </label>
                        <select
                          className="admin-form__select"
                          value={formData.reminderSchedule || '24H_AND_2H_BEFORE'}
                          onChange={(e) => updateField('reminderSchedule', e.target.value)}
                          style={{
                            fontSize: '12.5px',
                            padding: '9px 12px',
                            backgroundColor: '#FFFFFF',
                            borderColor: '#CBD5E1',
                            borderRadius: '8px',
                          }}
                        >
                          <option value="24H_AND_2H_BEFORE">24 Hours & 2 Hours Before Due Date (Recommended)</option>
                          <option value="48H_AND_24H_BEFORE">48 Hours & 24 Hours Before Due Date</option>
                          <option value="7D_3D_1D_BEFORE">Weekly, 3 Days, and 1 Day Before Due Date</option>
                          <option value="DAILY_UNTIL_COMPLETED">Daily Reminder until Learner Submits</option>
                        </select>
                      </div>

                      {/* Delivery Channels */}
                      <div className="admin-form__group" style={{ margin: 0 }}>
                        <label className="admin-form__label" style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                          📡 Delivery Channels
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[
                            { id: 'EMAIL', label: 'Email', icon: '✉️' },
                            { id: 'IN_APP', label: 'In-App Portal', icon: '🔔' },
                            { id: 'SMS', label: 'SMS / Push', icon: '📱' },
                          ].map((ch) => {
                            const currentChannels = formData.reminderChannels || ['EMAIL', 'IN_APP'];
                            const isChecked = currentChannels.includes(ch.id);
                            return (
                              <button
                                key={ch.id}
                                type="button"
                                onClick={() => {
                                  const updated = isChecked
                                    ? currentChannels.filter((c) => c !== ch.id)
                                    : [...currentChannels, ch.id];
                                  updateField('reminderChannels', updated.length > 0 ? updated : ['IN_APP']);
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '7px 12px',
                                  borderRadius: '8px',
                                  border: isChecked ? '1.5px solid #4F46E5' : '1px solid #CBD5E1',
                                  backgroundColor: isChecked ? '#EEF2FF' : '#FFFFFF',
                                  color: isChecked ? '#3730A3' : '#64748B',
                                  fontSize: '12px',
                                  fontWeight: isChecked ? 600 : 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <span>{ch.icon}</span>
                                <span>{ch.label}</span>
                                {isChecked && <span style={{ fontSize: '11px', color: '#4F46E5' }}>✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Reporting Manager Escalation Option */}
                    <div
                      style={{
                        padding: '12px 14px',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.notifyManagerOnOverdue ?? true}
                          onChange={(e) => updateField('notifyManagerOnOverdue', e.target.checked)}
                          style={{ width: '16px', height: '16px', accentColor: '#4F46E5', cursor: 'pointer' }}
                        />
                        <div>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#1E293B' }}>
                            Escalate to Reporting Manager on Overdue
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#64748B' }}>
                            Automatically notifies the learner's assigned manager if the submission deadline passes without a passing score.
                          </div>
                        </div>
                      </label>
                      <span style={{ fontSize: '11px', color: '#6366F1', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        👔 Manager Oversight
                      </span>
                    </div>

                    {/* Preview Bubble */}
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: '8px',
                        backgroundColor: '#EFF6FF',
                        border: '1px dashed #93C5FD',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '11.5px',
                        color: '#1E40AF',
                      }}
                    >
                      <span>💬</span>
                      <span>
                        <strong>Sample Notification:</strong> "Reminder: Your required simulation <em>'{formData.title || 'Scenario'}'</em> is due {formData.dueAt ? `on ${formData.dueAt.replace('T', ' ')}` : 'soon'}. Click to launch and complete your session."
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Attempts & Simulation Pacing Policy */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
                3. Attempts, Pacing & Time-Out Rules
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div className="admin-form__group">
                  <label className="admin-form__label">Max Learner Attempts</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    className="admin-form__input"
                    value={formData.maxAttempts}
                    onChange={(e) => updateField('maxAttempts', Number(e.target.value) || 3)}
                  />
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Attempt Cool-Off (Hours)</label>
                  <input
                    type="number"
                    min="0"
                    max="168"
                    className="admin-form__input"
                    value={formData.attemptGapHours ?? 0}
                    onChange={(e) => updateField('attemptGapHours', Number(e.target.value) || 0)}
                  />
                  <span style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', display: 'block' }}>
                    0 = Immediate retries allowed.
                  </span>
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Call Duration Limit (Mins)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    className="admin-form__input"
                    value={formData.timeLimitMinutes}
                    onChange={(e) => updateField('timeLimitMinutes', Number(e.target.value) || 10)}
                  />
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Time-Out Action</label>
                  <select
                    className="admin-form__select"
                    value={formData.timeOutAction || 'WARN_1MIN'}
                    onChange={(e) => updateField('timeOutAction', e.target.value as any)}
                  >
                    <option value="WARN_1MIN">Warn at 1 min left & auto-submit (Standard)</option>
                    <option value="AUTO_SUBMIT">Hard auto-submit when timer reaches zero</option>
                    <option value="EXTEND_GRACE">Allow 2-minute grace wrap-up period</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginTop: '8px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#F8FAFC',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.allowResume ?? true}
                    onChange={(e) => updateField('allowResume', e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <strong>Allow Learner to Resume In-Progress Simulation</strong>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>
                      Learner can reconnect if browser refreshes or network drops without losing attempt.
                    </div>
                  </div>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#F8FAFC',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.startingCountsAsAttempt ?? true}
                    onChange={(e) => updateField('startingCountsAsAttempt', e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <strong>Deduct Attempt Count Immediately on Start</strong>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>
                      Prevents abandoning mid-call to circumvent attempt limits.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Releasing Results & Scoring Policy */}
            <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
                4. Evaluation & Score Policy
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                <div className="admin-form__group">
                  <label className="admin-form__label">Passing Benchmark Score (%)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="admin-form__input"
                    value={formData.passScore}
                    onChange={(e) => updateField('passScore', Number(e.target.value) || 70)}
                  />
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Releasing Results Timing</label>
                  <select
                    className="admin-form__select"
                    value={formData.releaseResultsTiming || 'IMMEDIATELY'}
                    onChange={(e) => updateField('releaseResultsTiming', e.target.value as any)}
                  >
                    <option value="IMMEDIATELY">Immediately after simulation completes</option>
                    <option value="AFTER_DEADLINE">Only after scenario deadline passes</option>
                    <option value="MANUAL">Manual release by organization admin / trainer</option>
                  </select>
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Final Score Counting Policy</label>
                  <select
                    className="admin-form__select"
                    value={formData.scoreCountingPolicy || 'BEST'}
                    onChange={(e) => updateField('scoreCountingPolicy', e.target.value as any)}
                  >
                    <option value="BEST">Best Attempt (Highest Score)</option>
                    <option value="LATEST">Latest Attempt Score</option>
                    <option value="AVERAGE">Average Across All Attempts</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: PERSONA & BRIEFING (CLEAR SEPARATION: LEARNER SECTION vs AI COUNTERPARTY SECTION) */}
        {activeTab === 'persona' && (
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* ========================================================================= */}
            {/* SECTION 1: 🧑‍💼 LEARNER PROFILE & SIMULATION BRIEFING                      */}
            {/* Everything the human learner experiences, sees, and uses in the call      */}
            {/* ========================================================================= */}
            <div
              style={{
                background: '#FFFFFF',
                border: '1.5px solid #E2E8F0',
                borderRadius: '14px',
                padding: '24px',
                display: 'grid',
                gap: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              }}
            >
              {/* Section 1 Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  borderBottom: '1.5px solid #F1F5F9',
                  paddingBottom: '16px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: '#EEF2FF',
                      color: '#4F46E5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      border: '1px solid #C7D2FE',
                    }}
                  >
                    🧑‍💼
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B', margin: 0 }}>
                        Section 1: Learner Role, Mission & Briefing Dossier
                      </h3>
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          backgroundColor: '#EEF2FF',
                          color: '#4338CA',
                          fontWeight: 700,
                          border: '1px solid #C7D2FE',
                        }}
                      >
                        Learner-Facing
                      </span>
                    </div>
                    <p style={{ fontSize: '12.5px', color: '#64748B', margin: '3px 0 0' }}>
                      Defines what the human learner plays as, what their task is, and what documents they can study before starting the simulation.
                    </p>
                  </div>
                </div>
              </div>

              {/* 1. Learner Acting Role in Simulation (Searchable Combobox) */}
              <LearnerRoleSelector
                learnerRole={formData.learnerRole || 'Customer Support Representative'}
                onLearnerRoleChange={(role) => updateField('learnerRole', role)}
              />

              {/* 2. Learner Mission & Objectives */}
              <div className="admin-form__group">
                <label className="admin-form__label" style={{ fontWeight: 600, color: '#1E293B' }}>
                  🎯 Learner Mission & Core Objective *
                </label>
                <input
                  type="text"
                  className="admin-form__input"
                  placeholder="e.g. Discover deposit horizon, explain rate drop transparently, register nominee"
                  value={formData.learnerObjective || ''}
                  onChange={(e) => updateField('learnerObjective', e.target.value)}
                  style={{ backgroundColor: '#FFFFFF' }}
                  required
                />
                <span style={{ fontSize: '11px', color: '#64748B', marginTop: '3px', display: 'block' }}>
                  Displayed in the learner's pre-call briefing card as their primary task.
                </span>
              </div>

              {/* 3. Preflight Case Notes / Background */}
              <div className="admin-form__group">
                <label className="admin-form__label" style={{ fontWeight: 600, color: '#1E293B' }}>
                  📋 Preflight Case Notes & Detailed Background
                </label>
                <textarea
                  rows={3}
                  className="admin-form__textarea"
                  placeholder="Detailed customer relationship history, transaction records, branch timeline, and account context..."
                  value={formData.scenarioBackground || ''}
                  onChange={(e) => updateField('scenarioBackground', e.target.value)}
                  style={{ backgroundColor: '#FFFFFF' }}
                />
                <span style={{ fontSize: '11px', color: '#64748B', marginTop: '3px', display: 'block' }}>
                  Context dossier visible to the learner prior to initiating the call.
                </span>
              </div>

              {/* 4. Reference Documents & Knowledge Base Files with Cloudinary Upload */}
              <div
                style={{
                  borderRadius: '10px',
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#F8FAFC',
                  padding: '18px',
                  display: 'grid',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <label className="admin-form__label" style={{ margin: 0, fontWeight: 700, color: '#1E293B', fontSize: '13px' }}>
                      📄 Reference Documents & Knowledge Base Files
                    </label>
                    <p style={{ fontSize: '11.5px', color: '#64748B', margin: '2px 0 0' }}>
                      Upload actual documents (.pdf, .docx, .txt, .csv) to Cloudinary or paste public knowledge base URLs for learner preflight access.
                    </p>
                  </div>
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600 }}>
                    ☁️ Cloudinary & CDN Storage
                  </span>
                </div>

                {/* Upload & Link Controls */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={docFileInputRef}
                    onChange={handleDocFileUpload}
                    accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                  />

                  {/* Upload Button */}
                  <button
                    type="button"
                    onClick={() => docFileInputRef.current?.click()}
                    disabled={isUploadingDoc}
                    className="admin-btn admin-btn--primary"
                    style={{
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12.5px',
                      padding: '8px 14px',
                    }}
                  >
                    {isUploadingDoc ? (
                      <>
                        <span className="spinner" style={{ width: '14px', height: '14px', border: '2px solid #FFF', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                        Uploading to Cloud...
                      </>
                    ) : (
                      <>
                        <span>📁</span>
                        <span>Upload File (.pdf, .docx, .txt)</span>
                      </>
                    )}
                  </button>

                  <span style={{ fontSize: '12px', color: '#94A3B8' }}>or</span>

                  {/* Manual URL / Title Input */}
                  <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '240px' }}>
                    <input
                      type="text"
                      className="admin-form__input"
                      placeholder="Paste document URL (https://...) or enter reference manual title..."
                      value={docInput}
                      onChange={(e) => setDocInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDoc();
                        }
                      }}
                      style={{ fontSize: '12.5px', backgroundColor: '#FFFFFF' }}
                    />
                    <button
                      type="button"
                      onClick={handleAddDoc}
                      className="admin-btn admin-btn--secondary"
                      style={{ whiteSpace: 'nowrap', fontSize: '12.5px' }}
                    >
                      + Add Link
                    </button>
                  </div>
                </div>

                {/* Uploaded / Attached Documents List */}
                {formData.referenceDocuments && formData.referenceDocuments.length > 0 ? (
                  <div style={{ display: 'grid', gap: '8px', marginTop: '6px' }}>
                    {formData.referenceDocuments.map((doc, idx) => {
                      const isUrl = doc.startsWith('http://') || doc.startsWith('https://') || doc.startsWith('/api/v1/media');
                      const fileName = isUrl
                        ? decodeURIComponent(doc.split('/').pop()?.split('?')[0] || doc)
                        : doc;

                      return (
                        <div
                          key={`${doc}-${idx}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 14px',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #BFDBFE',
                            fontSize: '12px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                            <span style={{ fontSize: '16px' }}>📄</span>
                            <span style={{ fontWeight: 600, color: '#1E40AF', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                              {fileName}
                            </span>
                            {isUrl && (
                              <a
                                href={doc}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '11px',
                                  color: '#2563EB',
                                  textDecoration: 'underline',
                                  marginLeft: '4px',
                                  fontWeight: 500,
                                }}
                              >
                                🔗 Open / View
                              </a>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isUrl && (
                              <span style={{ fontSize: '10.5px', padding: '2px 6px', borderRadius: '4px', backgroundColor: '#DCFCE7', color: '#166534', fontWeight: 600 }}>
                                ☁️ Stored
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveDoc(doc)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontWeight: 700, padding: '2px 6px', fontSize: '13px' }}
                              title="Delete document attachment"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span style={{ fontSize: '11px', color: '#94A3B8', fontStyle: 'italic' }}>
                    No reference files attached. The simulation will run without external document dependencies.
                  </span>
                )}
              </div>
            </div>

            {/* ========================================================================= */}
            {/* SECTION 2: 🤖 AI COUNTERPARTY PERSONA, VOICE & BEHIND-THE-SCENES RULES    */}
            {/* Everything configuring the AI persona, voice, rules, and compliance        */}
            {/* ========================================================================= */}
            <div
              style={{
                background: '#FFFFFF',
                border: '1.5px solid #E2E8F0',
                borderRadius: '14px',
                padding: '24px',
                display: 'grid',
                gap: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              }}
            >
              {/* Section 2 Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  borderBottom: '1.5px solid #F1F5F9',
                  paddingBottom: '16px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: '#F0FDF4',
                      color: '#16A34A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      border: '1px solid #BBF7D0',
                    }}
                  >
                    🤖
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B', margin: 0 }}>
                        Section 2: AI Counterparty Persona, Voice & Grounding
                      </h3>
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          backgroundColor: '#F0FDF4',
                          color: '#15803D',
                          fontWeight: 700,
                          border: '1px solid #BBF7D0',
                        }}
                      >
                        AI Counterparty
                      </span>
                    </div>
                    <p style={{ fontSize: '12.5px', color: '#64748B', margin: '3px 0 0' }}>
                      Configures the AI character(s) (customer/client), avatar visual representation, voice synthesis, objections, hidden facts, company policies, and safety guardrails.
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Policies Adder (AI Enforced Behind-The-Scenes Rules) */}
              <div className="admin-form__group">
                <label className="admin-form__label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                  <span style={{ fontWeight: 700, color: '#1E293B' }}>⚖️ Company Policies & Compliance Rules (Enforced by AI)</span>
                  <span style={{ fontSize: '11px', color: '#64748B' }}>
                    Enforced in AI persona prompt during call (omitted if empty)
                  </span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="admin-form__input"
                    placeholder="e.g. Max FD tenure is 10 years; Nominee disclosure is compulsory for deposits > Rs 5L; Standard refund window is 48 hours..."
                    value={policyInput}
                    onChange={(e) => setPolicyInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPolicy();
                      }
                    }}
                    style={{ backgroundColor: '#FFFFFF' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddPolicy}
                    className="admin-btn admin-btn--secondary"
                    style={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                  >
                    + Add Policy
                  </button>
                </div>
                {formData.companyPolicies && formData.companyPolicies.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {formData.companyPolicies.map((pol) => (
                      <span
                        key={pol}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          backgroundColor: '#FEF3C7',
                          color: '#92400E',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid #FDE68A',
                        }}
                      >
                        <span>⚖️ {pol}</span>
                        <button
                          type="button"
                          onClick={() => handleRemovePolicy(pol)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontWeight: 700, padding: '0 2px' }}
                          title="Remove policy"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px', display: 'block', fontStyle: 'italic' }}>
                    No custom policies added. The AI will rely on standard scenario background.
                  </span>
                )}
              </div>

              {/* Persona Editor Component (AI Identity, Voice, Objections, Hidden Facts, Guardrails, Ending Rules) */}
              <PersonaEditor
                persona={formData.persona || defaultPersona}
                personas={formData.personas || (formData.persona ? [formData.persona] : [defaultPersona])}
                learnerRole={formData.learnerRole || 'Customer Support Representative'}
                onChange={(updated) => {
                  setFormData((prev) => ({
                    ...prev,
                    persona: updated,
                    country: updated.country || (prev as any).country,
                    countryName: updated.countryName || (prev as any).countryName,
                    tone: updated.tone || (prev as any).tone,
                    ttsVoice: updated.ttsVoice || (prev as any).ttsVoice,
                    ttsProvider: updated.ttsProvider || (prev as any).ttsProvider,
                    speakingPace: updated.speakingPace !== undefined ? updated.speakingPace : (prev as any).speakingPace,
                    pitchPreference: updated.pitchPreference !== undefined ? updated.pitchPreference : (prev as any).pitchPreference,
                  }));
                }}
                onPersonasChange={(updatedList) => {
                  setFormData((prev) => {
                    const primary = updatedList[0];
                    return {
                      ...prev,
                      personas: updatedList,
                      persona: primary || prev.persona,
                      country: primary?.country || (prev as any).country,
                      countryName: primary?.countryName || (prev as any).countryName,
                      tone: primary?.tone || (prev as any).tone,
                      ttsVoice: primary?.ttsVoice || (prev as any).ttsVoice,
                      ttsProvider: primary?.ttsProvider || (prev as any).ttsProvider,
                      speakingPace: primary?.speakingPace !== undefined ? primary.speakingPace : (prev as any).speakingPace,
                      pitchPreference: primary?.pitchPreference !== undefined ? primary.pitchPreference : (prev as any).pitchPreference,
                    };
                  });
                }}
                onLearnerRoleChange={(role) => updateField('learnerRole', role)}
              />
            </div>
          </div>
        )}

        {/* TAB 3: OBJECTIVES */}
        {activeTab === 'objectives' && (
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Objectives & Guidance</h3>
            <ObjectiveEditor
              objectives={formData.objectives || []}
              guidelines={formData.guidelines || []}
              onChange={(updated) => updateField('objectives', updated)}
              onGuidelinesChange={(updated) => updateField('guidelines', updated)}
            />
          </div>
        )}

        {/* TAB 4: RUBRIC & COMPETENCIES */}
        {activeTab === 'rubric' && (
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Scoring Rubrics</h3>
            <RubricEditor
              rubrics={formData.rubrics || {}}
              onChange={(updated) => updateField('rubrics', updated)}
            />
          </div>
        )}

        {/* TAB 5: SIMULATION SETTINGS & LANGUAGES */}
        {activeTab === 'settings' && (
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px', display: 'grid', gap: '20px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Language & Speech Calibration</h3>

            {/* Supported Languages Multi-select & Super Admin Custom Adder */}
            <div className="admin-form__group">
              <label className="admin-form__label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Allowed Simulation Languages</span>
                <span style={{ fontSize: '12px', fontWeight: 400, color: '#64748B' }}>
                  Select all languages supported by the AI persona and speech engine.
                </span>
              </label>

              {/* Standard List */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginTop: '8px' }}>
                {DEFAULT_LANGUAGES.map((l) => {
                  const isChecked = (formData.allowedLanguages || []).includes(l.code);
                  return (
                    <label
                      key={l.code}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: isChecked ? '1.5px solid #4F46E5' : '1px solid #E2E8F0',
                        backgroundColor: isChecked ? '#EEF2FF' : '#F8FAFC',
                        color: isChecked ? '#312E81' : '#334155',
                        fontWeight: isChecked ? 600 : 400,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontSize: '12.5px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleLanguage(l.code)}
                        style={{ width: '16px', height: '16px', accentColor: 'var(--blue)' }}
                      />
                      <span>{l.label}</span>
                    </label>
                  );
                })}
              </div>

              {/* Super Admin Custom Language Adder */}
              <div
                style={{
                  marginTop: '16px',
                  padding: '14px',
                  borderRadius: '10px',
                  border: '1px dashed #CBD5E1',
                  backgroundColor: '#F8FAFC',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '12.5px', color: '#334155' }}>
                    Super Admin: Add Custom Language / Locale Tag
                  </strong>
                  <span style={{ fontSize: '11px', color: '#64748B' }}>
                    e.g. <code>te-IN</code>, <code>mr-IN</code>, <code>zh-CN</code>, <code>pt-BR</code>
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter custom language code or name..."
                    value={customLangInput}
                    onChange={(e) => setCustomLangInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomLanguage();
                      }
                    }}
                    className="admin-form__input"
                    style={{ fontSize: '12.5px', flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomLanguage}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#4F46E5',
                      color: '#FFFFFF',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    + Add Language
                  </button>
                </div>

                {/* Custom Tags Pill List */}
                {formData.allowedLanguages && formData.allowedLanguages.some(l => !DEFAULT_LANGUAGES.map(dl => dl.code).includes(l)) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingTop: '4px' }}>
                    {formData.allowedLanguages
                      .filter(l => !DEFAULT_LANGUAGES.map(dl => dl.code).includes(l))
                      .map((customLang) => (
                        <span
                          key={customLang}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#EEF2FF',
                            color: '#3730A3',
                            fontSize: '12px',
                            fontWeight: 600,
                            border: '1px solid #C7D2FE',
                          }}
                        >
                          {customLang}
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomLanguage(customLang)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontWeight: 700, padding: 0 }}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Code Switching & Feedback Language */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', borderTop: '1px solid var(--line)', paddingTop: '16px' }}>
              <div className="admin-form__group">
                <label className="admin-form__label">Feedback & Scorecard Language</label>
                <select
                  className="admin-form__select"
                  value={formData.feedbackLanguage || 'en'}
                  onChange={(e) => updateField('feedbackLanguage', e.target.value)}
                >
                  <option value="en">English (Official Scorecard)</option>
                  <option value="hi">Hindi (हिंदी)</option>
                  <option value="ta">Tamil (தமிழ்)</option>
                  <option value="te">Telugu (తెలుగు)</option>
                  <option value="mr">Marathi (मराठी)</option>
                </select>
                <span style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', display: 'block' }}>
                  Language used when generating post-call evaluation reports.
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#F8FAFC',
                    cursor: 'pointer',
                    fontSize: '13px',
                    width: '100%',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.allowMixedLanguageReplies ?? true}
                    onChange={(e) => updateField('allowMixedLanguageReplies', e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <strong>Allow Mixed-Language Replies & Code-Switching</strong>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>
                      AI naturally accepts and mirrors Hinglish, dialect phrases, or casual code-switching.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Hardware & Diagnostics Pre-Flight Checklist Controls */}
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: '16px', marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
                    Pre-Flight System Check & Device Permissions
                  </h4>
                  <p style={{ fontSize: '12.5px', color: 'var(--muted)', margin: '4px 0 0' }}>
                    Configure compulsory hardware and browser permissions required to start or resume this scenario.
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                {/* Video / Camera Toggle */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: formData.requiresCamera ? '1px solid var(--blue)' : '1px solid var(--line)',
                    background: formData.requiresCamera ? 'var(--blue-soft)' : 'var(--surface)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(formData.requiresCamera)}
                    onChange={(e) => updateField('requiresCamera', e.target.checked)}
                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '15px' }}>📹</span>
                      <strong style={{ fontSize: '13px', color: 'var(--ink)' }}>Camera Feed & Video Telemetry</strong>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.4 }}>
                      {formData.requiresCamera ? 'Compulsory (Simulation blocked if camera unavailable)' : 'Optional (Audio-only allowed)'}
                    </p>
                  </div>
                </label>

                {/* Local Face & Video Analysis (face-api.js) Toggle */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: formData.videoAnalysisEnabled && organizationAllowsVideoAnalysis ? '1px solid var(--blue)' : '1px solid var(--line)',
                    background: formData.videoAnalysisEnabled && organizationAllowsVideoAnalysis ? 'var(--blue-soft)' : 'var(--surface)',
                    opacity: !organizationAllowsVideoAnalysis ? 0.65 : 1,
                    cursor: !organizationAllowsVideoAnalysis ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  title={!organizationAllowsVideoAnalysis ? 'Video analysis is disabled by organization policy in Admin Organization Settings.' : undefined}
                >
                  <input
                    type="checkbox"
                    disabled={!organizationAllowsVideoAnalysis}
                    checked={Boolean(formData.videoAnalysisEnabled && organizationAllowsVideoAnalysis)}
                    onChange={(e) => updateField('videoAnalysisEnabled', e.target.checked)}
                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '15px' }}>👤</span>
                      <strong style={{ fontSize: '13px', color: 'var(--ink)' }}>Video Analysis (face-api.js)</strong>
                      {!organizationAllowsVideoAnalysis && (
                        <span style={{ fontSize: '10px', color: 'var(--warn)', background: 'var(--warn-soft)', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                          Org Disabled
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.4 }}>
                      {!organizationAllowsVideoAnalysis
                        ? 'Disabled by organization policy. An organization administrator must enable Video Analysis in Settings.'
                        : (formData.videoAnalysisEnabled
                            ? 'Active — Runs locally in browser. No raw video frames are sent to server.'
                            : 'Disabled — No facial presence telemetry recorded.')}
                    </p>
                  </div>
                </label>

                {/* Configurable Video Analysis Parameters Panel */}
                {Boolean(formData.videoAnalysisEnabled && organizationAllowsVideoAnalysis) && (
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: 'var(--white)',
                      border: '1px solid var(--line)',
                      display: 'grid',
                      gap: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--navy)' }}>
                        Camera Analysis Parameters
                      </span>
                      <span style={{ fontSize: '11px', color: 'var(--muted)', background: 'var(--surface)', padding: '2px 6px', borderRadius: '4px' }}>
                        TinyFaceDetector (320px)
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                      {/* Sampling Interval */}
                      <div>
                        <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                          Sampling Rate
                        </label>
                        <select
                          value={formData.videoAnalysisConfig?.samplingIntervalMs || 2000}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            updateField('videoAnalysisConfig', {
                              ...(formData.videoAnalysisConfig || {}),
                              samplingIntervalMs: val,
                            });
                          }}
                          className="admin-form__select"
                          style={{ fontSize: '12.5px', padding: '6px 8px', width: '100%' }}
                        >
                          <option value={1000}>Every 1.0s (Fast)</option>
                          <option value={2000}>Every 2.0s (Recommended)</option>
                          <option value={3000}>Every 3.0s (Balanced)</option>
                          <option value={5000}>Every 5.0s (Low CPU)</option>
                        </select>
                      </div>

                      {/* Detection Confidence Threshold */}
                      <div>
                        <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                          Score Threshold
                        </label>
                        <select
                          value={formData.videoAnalysisConfig?.scoreThreshold || 0.55}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            updateField('videoAnalysisConfig', {
                              ...(formData.videoAnalysisConfig || {}),
                              scoreThreshold: val,
                            });
                          }}
                          className="admin-form__select"
                          style={{ fontSize: '12.5px', padding: '6px 8px', width: '100%' }}
                        >
                          <option value={0.4}>0.40 (Sensitive)</option>
                          <option value={0.55}>0.55 (Standard)</option>
                          <option value={0.7}>0.70 (Strict)</option>
                        </select>
                      </div>

                      {/* No-Face Warning Threshold */}
                      <div>
                        <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                          Out-of-Frame Warning
                        </label>
                        <select
                          value={formData.videoAnalysisConfig?.noFaceWarningSeconds || 10}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            updateField('videoAnalysisConfig', {
                              ...(formData.videoAnalysisConfig || {}),
                              noFaceWarningSeconds: val,
                            });
                          }}
                          className="admin-form__select"
                          style={{ fontSize: '12.5px', padding: '6px 8px', width: '100%' }}
                        >
                          <option value={5}>5 seconds</option>
                          <option value={10}>10 seconds (Default)</option>
                          <option value={15}>15 seconds</option>
                          <option value={20}>20 seconds</option>
                        </select>
                      </div>
                    </div>

                    {/* Checkbox Toggles */}
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', paddingTop: '4px', borderTop: '1px solid var(--line)' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.videoAnalysisConfig?.enableLandmarks ?? true}
                          onChange={(e) => {
                            updateField('videoAnalysisConfig', {
                              ...(formData.videoAnalysisConfig || {}),
                              enableLandmarks: e.target.checked,
                            });
                          }}
                          style={{ accentColor: 'var(--blue)' }}
                        />
                        <span>Facial Landmarks (68-pt)</span>
                      </label>

                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(formData.videoAnalysisConfig?.enableHeadPose)}
                          onChange={(e) => {
                            updateField('videoAnalysisConfig', {
                              ...(formData.videoAnalysisConfig || {}),
                              enableHeadPose: e.target.checked,
                              enableLandmarks: e.target.checked ? true : (formData.videoAnalysisConfig?.enableLandmarks ?? true),
                            });
                          }}
                          style={{ accentColor: 'var(--blue)' }}
                        />
                        <span>Head-Pose Observation</span>
                      </label>

                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(formData.videoAnalysisConfig?.enableExpressionAnalysis)}
                          onChange={(e) => {
                            updateField('videoAnalysisConfig', {
                              ...(formData.videoAnalysisConfig || {}),
                              enableExpressionAnalysis: e.target.checked,
                            });
                          }}
                          style={{ accentColor: 'var(--blue)' }}
                        />
                        <span>Facial Expressions (Approximate)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Audio / Microphone Toggle */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: formData.requiresMicrophone ? '1px solid var(--blue)' : '1px solid var(--line)',
                    background: formData.requiresMicrophone ? 'var(--blue-soft)' : 'var(--surface)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(formData.requiresMicrophone)}
                    onChange={(e) => updateField('requiresMicrophone', e.target.checked)}
                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '15px' }}>🎙️</span>
                      <strong style={{ fontSize: '13px', color: 'var(--ink)' }}>Microphone & Voice Engine</strong>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.4 }}>
                      {formData.requiresMicrophone ? 'Compulsory (Microphone permission required)' : 'Optional (Text-based mode permitted)'}
                    </p>
                  </div>
                </label>

                {/* Speaker Audio Playback Toggle */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: formData.requiresSpeaker ? '1px solid var(--blue)' : '1px solid var(--line)',
                    background: formData.requiresSpeaker ? 'var(--blue-soft)' : 'var(--surface)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(formData.requiresSpeaker)}
                    onChange={(e) => updateField('requiresSpeaker', e.target.checked)}
                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: 'var(--blue)' }}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '15px' }}>🔊</span>
                      <strong style={{ fontSize: '13px', color: 'var(--ink)' }}>Speaker Output Verification</strong>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.4 }}>
                      {formData.requiresSpeaker ? 'Compulsory (Audio test chime before start)' : 'Optional'}
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: PREVIEW */}
        {activeTab === 'preview' && (
          <div>
            <ScenarioPreview scenario={{ ...formData, status: initialValues?.status || 'DRAFT' }} />
          </div>
        )}

        {/* Action Bar */}
        <div
          className="scenario-form-actions"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            borderTop: '1px solid var(--line)',
            paddingTop: '16px',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="admin-btn admin-btn--secondary"
            style={{ minHeight: '40px' }}
          >
            Cancel
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={isSubmitting}
              className="admin-btn admin-btn--secondary"
              style={{ minHeight: '40px' }}
            >
              {isSubmitting ? 'Saving...' : 'Save Draft'}
            </button>

            {onPublish && (
              <button
                type="button"
                onClick={handlePublishClick}
                disabled={isSubmitting}
                className="admin-btn admin-btn--primary"
                style={{ minHeight: '40px' }}
              >
                Publish Scenario →
              </button>
            )}
          </div>
        </div>
      </form>

      {/* AI Scenario Generator Modal */}
      <AiScenarioGeneratorModal
        isOpen={isGeneratorOpen}
        onClose={() => setIsGeneratorOpen(false)}
        onApplyBlueprint={handleApplyBlueprint}
      />

      {/* Import Scenario from JSON Modal */}
      <ImportScenarioJsonModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        currentScenario={initialValues?.id ? formData : undefined}
        onImport={(importedDto) => {
          setFormData(importedDto);
          showToast('Scenario data loaded into form from JSON!', 'success');
        }}
      />

      {/* Best-Case Script Modal */}
      <BestCaseScriptModal
        isOpen={isBestCaseScriptOpen}
        onClose={() => setIsBestCaseScriptOpen(false)}
        scenarioId={initialValues?.id}
        scenarioTitle={formData.title}
        category={formData.category}
        passScore={formData.passScore}
        timeLimitMinutes={formData.timeLimitMinutes}
        learnerRole={formData.learnerRole}
        learnerObjective={formData.learnerObjective}
        personaName={formData.persona?.name || ''}
        personaRole={formData.persona?.role || ''}
        openingStatement={formData.persona?.openingStatement || ''}
        objectives={formData.objectives}
        guidelines={formData.guidelines}
        scenarioBackground={formData.scenarioBackground || formData.description}
        stages={formData.stages}
      />
    </div>
  );
};
